import { action, flow, observable } from "mobx";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { UploadDocumentResponse } from "../../../../api/documents/uploadDocument";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { useStore2 } from "../../../../WSUtils/useStore";
import { concatClassnames } from "../../../../WSUtils/util";
import { WSFormGrid } from "../../../../WSUtils/WSFormGrid";
import { CVEditContext } from "../../CVEditContext";
import { CVEditInvalidStateReminder } from "../../CVEditInvalidStateReminder";
import { CVEditDocumentEditor } from "../Components/CVEditDocumentEditor";
import { saveCurrentlyEditingDocumentAndCloseEditor } from "../Components/saveCurrentlyEditingDocumentAndCloseEditor";
import { CVEditAttachmentPicker } from "./CVEditAttachmentPicker";
import { CVEditAttachmentsList } from "./CVEditAttachmentsList";
import { CVEditAttachmentsSegmentStore } from "./CVEditAttachmentsSegmentStore";

export const CVEditAttachmentsSegment = observer(() => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const queryClient = useQueryClient();
  const L = useLocStore(LocContext.CV);

  const attachmentsSegmentStore = useStore2(
    "CVEditAttachmentsSegmentStore",
    (dt) =>
      new CVEditAttachmentsSegmentStore({
        name: "CVEditAttachmentsSegmentStore",

        parentDt: dt,
        useHooks: (t: CVEditAttachmentsSegmentStore) => {
          t.userInfo = useContext(UserInfoContext);
          t.queryClient = useQueryClient();
          t.cvEditContext = useContext(CVEditContext);
        },
      })
  );

  /* @observable documentEditorStore: CVEditDocumentEditorStore = (() => {
    const autorunEditor = () => {
      this.dt.disposers.push(
        autorun(() => {
          this.addingDocument;

          runInAction(() => {
            this.documentEditorStore?.dt?.dispose();
            this.documentEditorStore = null;

            if (this.addingDocument) {
              this.documentEditorStore = new CVEditDocumentEditorStore({
                name: "CVEditDocumentEditorStore",
                getUserInfo: this.getUserInfo,
                getCvEditContext: this.getCvEditContext,
                parentDt: this.dt,
                getFile: () => this.file,
                showEditor: () => this.showEditor,
                onCloseEditor: action(() => {
                  this.showAttachmentPickerHint = false;
                }),
              });
            }
          });
        })
      );
    };

    this.dt.disposers.push(
      when(() => !!this.documentsField.store.value, autorunEditor)
    );

    return null;
  })();*/

  const saving = useMemo(() => observable.box(false), []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDocumentPicker,
  } = useDropzone({
    multiple: false,
    noClick: true,
    onDrop: action((acceptedFiles) => {
      attachmentsSegmentStore.onFileAccepted(acceptedFiles[0]);
    }),
  });

  return (
    <CVEditAttachmentsSegmentStyles
      className="cve-attachments-segment cve-segment"
      {...getRootProps()}
    >
      <div className="cvp-h2 cve-header">
        {L.id("f75250653127").loc("Vedlegg")}
      </div>

      {!!attachmentsSegmentStore.showAttachmentList && (
        <CVEditAttachmentsList
          attachmentsSegmentStore={attachmentsSegmentStore}
        />
      )}

      {!!attachmentsSegmentStore.showAttachmentPicker && (
        <>
          <div className="cve-separator" />
          <CVEditAttachmentPicker
            attachmentsSegmentStore={attachmentsSegmentStore}
          />
        </>
      )}

      {!!attachmentsSegmentStore.showEditor && (
        <>
          <div className="cve-separator" />
          <CVEditDocumentEditor
            openDocumentPicker={openDocumentPicker}
            docEditorStore={null}
            headerText={L.id("bd16cb5365f2").loc("Legg ved dokument")}
          />
        </>
      )}

      <WSFormGrid
        float
        className={concatClassnames([
          "cve-button-bar",
          attachmentsSegmentStore.showPlaceholder &&
            "cve-button-bar-placeholder",
        ])}
      >
        <CVEditInvalidStateReminder
          showReminder={() => {
            /*const documentEditorStore =
              attachmentsSegmentStore.documentEditorStore;

            if (!documentEditorStore) return false;
            return documentEditorStore.visibleValidationErrors.length > 0;*/
            return false;
          }}
        />

        <Button
          icon="pi pi-upload"
          className={concatClassnames([
            "p-button p-button-outlined cve-attachment-add-button",
            attachmentsSegmentStore.showPlaceholder &&
              "cve-empty-list-placeholder cve-no-attachments-placeholder",
          ])}
          loading={saving.get()}
          label={
            attachmentsSegmentStore.showAttachmentPicker
              ? L.id("aed210908079").loc("Last opp nytt vedlegg")
              : attachmentsSegmentStore.showEditor
              ? L.id("49ddabff01d8").loc("Legg ved")
              : L.id("13c45eb4f2ed").loc("Legg ved dokumenter")
          }
          onClick={(evt) => {
            evt.preventDefault();

            if (attachmentsSegmentStore.showEditor) {
              const save = flow(function* () {
                saving.set(true);
                try {
                  const result: UploadDocumentResponse =
                    yield saveCurrentlyEditingDocumentAndCloseEditor({
                      userInfo,
                      cvEditContext,
                      queryClient,
                      file: attachmentsSegmentStore.file,
                      editorStore: null,
                    });

                  attachmentsSegmentStore.addPersistedAttachment(
                    result.item.id_dokumenter
                  );
                } finally {
                  saving.set(false);
                }
              });

              save();

              return false;
            }

            if (!attachmentsSegmentStore.showAttachmentPicker) {
              attachmentsSegmentStore.showAttachmentPickerHint = true;

              return false;
            }

            openDocumentPicker();

            return false;
          }}
        />
      </WSFormGrid>
      <input {...getInputProps()} />
    </CVEditAttachmentsSegmentStyles>
  );
});

const CVEditAttachmentsSegmentStyles = styled(WSFormGrid)`
  &.cve-attachments-segment {
    .cve-empty-list-placeholder.cve-empty-list-placeholder {
      height: 160px;
    }
  }
`;
